/**
 * Turns the month-by-month exposure trend into a plain-language reading a chief
 * can act on: what the metric is, what the worst month meant "at the time it
 * happened", and what the latest standing means "today". Deterministic and
 * rule-based (NOT a generative model), so the wording is auditable.
 *
 * Framing: CARCINOGENIC exposure = TOTAL VOC (the unspeciated aromatic load the
 * PID measures — a carcinogen indicator, never a benzene measurement). CO is
 * reported separately as an ACUTE hazard (poisoning). The headline tracks the
 * carcinogen (VOC); CO acute is called out alongside.
 */
import { CO_NIOSH_REL_PPM, VOC_SCREEN_PPM } from "@/lib/carcinogen";

export type ExposureMonth = {
  month: string; // "YYYY-MM"
  label: string; // short month label, e.g. "Jun"
  // CO acute exceedances (CO over its NIOSH REL) that month — reported as an
  // acute hazard, NOT part of the carcinogen count.
  co: number;
  // Total-VOC carcinogen flags that month (same as `total` — kept for the chart).
  voc: number;
  // The CARCINOGEN headline: firefighter-exposures with elevated total VOC that
  // month (one per firefighter per incident).
  total: number;
};

export type ExposureInsights = {
  hasData: boolean;
  /** One-line standing summary of the trailing window. */
  headline: string;
  /** How to read the metric itself. */
  about: string;
  /** Plain-language reading of the heaviest month. */
  atTheTime: string;
  /** What the latest month + trend direction means now. */
  today: string;
  /** Label of the heaviest month, or null when the window is clean. */
  peakLabel: string | null;
};

function exposures(n: number): string {
  return `${n} ${n === 1 ? "exposure" : "exposures"}`;
}

const ABOUT =
  `Each month counts the firefighter-exposures where total VOC (the carcinogen indicator) was elevated, one count per firefighter per incident. ` +
  `The BW Ultra's PID measures total (unspeciated) VOC, the aromatic products of incomplete combustion. We use it directly as the carcinogen signal and never claim to identify a single species. The real burden is the cumulative VOC dose (ppm·min) on each incident's exposure record. A peak over a small screen (${VOC_SCREEN_PPM} ppm) flags "aromatics present on scene: decon". ` +
  `CO is reported separately as an acute hazard: carbon-monoxide poisoning, not a carcinogen. A measured CO TWA ≥ ${CO_NIOSH_REL_PPM} ppm is over the NIOSH REL. H₂S and LEL are acute too. ` +
  `A count keeps the figure honest. It answers "how many of our people were exposed", not a percentage that balloons with call volume. Occupational exposure as a firefighter is an IARC Group 1 carcinogen.`;

/**
 * Build the interpretive summary from the trailing-window trend (oldest →
 * newest). Safe on an all-zero window (returns a "clean" reading).
 */
export function buildExposureInsights(
  months: ExposureMonth[],
): ExposureInsights {
  const rows = months;
  // "Has data" if there is any carcinogen (VOC) OR acute-CO activity to read.
  const hasData = rows.some((m) => m.total > 0 || m.co > 0);
  const windowLen = months.length;

  if (!hasData || rows.length === 0) {
    return {
      hasData: false,
      headline: `No elevated VOC or acute CO recorded in the last ${windowLen} months.`,
      about: ABOUT,
      atTheTime:
        "No firefighter-exposure showed elevated total VOC or an acute CO exceedance in this window. Nothing to interpret yet.",
      today:
        "Current standing is clean. Keep logging exposures so the trend stays meaningful for long-term cancer-risk tracking.",
      peakLabel: null,
    };
  }

  // Heaviest month by CARCINOGEN load (total VOC).
  const peak = rows.reduce((a, b) => (b.total > a.total ? b : a));
  const latest = rows[rows.length - 1];
  const prior = rows.slice(0, -1).filter((m) => m.total > 0);
  const priorAvg = prior.length
    ? prior.reduce((s, m) => s + m.total, 0) / prior.length
    : 0;
  const windowTotal = rows.reduce((s, m) => s + m.total, 0);
  const acuteCoWindow = rows.reduce((s, m) => s + m.co, 0);

  const headline = `Over the last ${windowLen} months, total VOC (the carcinogen indicator) was elevated on ${exposures(
    windowTotal,
  )}, the most in ${peak.label}.`;

  const atTheTime =
    `${peak.label} was the heaviest month for carcinogenic exposure: ${exposures(peak.total)} with elevated total VOC` +
    (peak.co > 0
      ? `, plus ${exposures(peak.co)} where CO went over its acute REL`
      : ``) +
    `. Total VOC is the unspeciated aromatic load, a carcinogen indicator. Confirm each against the incident's measured VOC dose (ppm·min) and decon.`;

  let direction: string;
  if (latest.total === 0) direction = "back to a clean baseline";
  else if (priorAvg === 0) direction = "the first elevated VOC in the window";
  else if (latest.total > priorAvg * 1.15)
    direction = "rising versus the earlier months";
  else if (latest.total < priorAvg * 0.85)
    direction = "easing versus the earlier months";
  else direction = "holding roughly steady versus the earlier months";

  const today =
    `Today (${latest.label}), total VOC was elevated on ${exposures(latest.total)}: ${direction}. ` +
    (latest.total > 0
      ? "Treat the flagged crews as carcinogen-exposed: confirm the VOC dose, then follow decon and medical-monitoring protocol."
      : "No elevated VOC this month; the standing carcinogen risk is carried by the cumulative history above, not today's reading.") +
    (acuteCoWindow > 0
      ? ` Separately, CO (acute poisoning) went over its REL on ${exposures(acuteCoWindow)} in this window. Review those for acute exposure.`
      : ``);

  return {
    hasData: true,
    headline,
    about: ABOUT,
    atTheTime,
    today,
    peakLabel: peak.label,
  };
}
